/**
 * PDP Datasheet custom element — Technical Drawing layout
 *
 * Handles layer controls tab switching for multi-drawing products.
 */

class PdpDatasheet extends HTMLElement {
  constructor() {
    super();
    this.layerTabs = this.querySelectorAll('.pdp-datasheet__layer-tab');
    this.multi = this.querySelector('[data-ds-multi]');
  }

  connectedCallback() {
    if (this.layerTabs.length) {
      this.initLayerControls();
    }
    if (this.multi) {
      this.initMultiColumn();
    }
  }

  /* ---------------------------------------------------------------
     Multi-column datasheet (AAS-978) — page size columns via arrows
  --------------------------------------------------------------- */

  initMultiColumn() {
    this.track = this.multi.querySelector('[data-ds-track]');
    this.viewport = this.multi.querySelector('[data-ds-viewport]');
    this.prevBtn = this.multi.querySelector('[data-ds-prev]');
    this.nextBtn = this.multi.querySelector('[data-ds-next]');
    this.columns = Array.from(this.multi.querySelectorAll('.ds-multi__col'));
    this.colIndex = 0;

    if (!this.track || !this.columns.length) return;

    this.prevBtn?.addEventListener('click', () => this.pageColumns(-1));
    this.nextBtn?.addEventListener('click', () => this.pageColumns(1));

    this._onResize = this.refreshMultiColumn.bind(this);
    window.addEventListener('resize', this._onResize);

    this.initFocusPaging();
    this.refreshMultiColumn();
  }

  // Fully visible columns only — floor, NOT round (AAS-5016): rounding 2.6
  // visible columns up to 3 made maxIndex claim the end one page early, so a
  // narrowed window could strand the last column partially clipped with the
  // next arrow already disabled. The 0.02 epsilon forgives subpixel noise.
  visibleCount() {
    const colWidth = this.columns[0]?.getBoundingClientRect().width || 1;
    const vpWidth = this.viewport?.clientWidth || 0;
    return Math.max(1, Math.floor(vpWidth / colWidth + 0.02));
  }

  // How far the track can translate: content flush with the viewport's right
  // edge. The pixel ground truth the paging end state derives from.
  maxTranslate() {
    const vpWidth = this.viewport?.clientWidth || 0;
    const trackWidth = this.track?.scrollWidth || 0;
    return Math.max(0, trackWidth - vpWidth);
  }

  // Column-steps needed to reach maxTranslate. Derived from the pixel limit
  // (not from visibleCount) so the last page always lands with the final
  // column fully in view; the 0.5px epsilon avoids a no-op extra step when
  // the limit is an exact column multiple.
  maxIndex() {
    const colWidth = this.columns[0]?.getBoundingClientRect().width || 1;
    return Math.max(0, Math.ceil((this.maxTranslate() - 0.5) / colWidth));
  }

  pageColumns(direction) {
    const next = this.colIndex + direction;
    this.colIndex = Math.min(this.maxIndex(), Math.max(0, next));
    this.applyMultiColumn();
  }

  applyMultiColumn() {
    const colWidth = this.columns[0]?.getBoundingClientRect().width || 0;
    // Clamp to the pixel limit: the final page aligns the content's end with
    // the viewport edge (a partial column may then peek on the LEFT, never
    // a clipped last column on the right).
    const translate = Math.min(this.colIndex * colWidth, this.maxTranslate());
    this.track.style.transform = `translateX(${-translate}px)`;

    const atStart = this.colIndex <= 0;
    const atEnd = this.colIndex >= this.maxIndex();
    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;

    // Edge fades (AAS-5016, reworked in AAS-5263): a fade only earns its place
    // when the column under that edge is genuinely clipped mid-column. Below
    // 1025px the columns exactly divide the viewport and paging moves by whole
    // columns, so both edges always land on a column boundary — the former
    // !atStart/!atEnd condition then painted the fade over fully visible
    // values and made them illegible. A boundary-aligned edge gets no fade;
    // the arrows keep signalling the remaining pages.
    const vpWidth = this.viewport?.clientWidth || 0;
    const EPS = 1; // px — forgives subpixel column widths vs integer clientWidth
    const onColumnBoundary = (x) => {
      if (colWidth <= 0) return true;
      const r = x % colWidth;
      return r < EPS || colWidth - r < EPS;
    };
    this.multi.classList.toggle('has-more-left', !atStart && !onColumnBoundary(translate));
    this.multi.classList.toggle('has-more-right', !atEnd && !onColumnBoundary(translate + vpWidth));
  }

  // Desktop only: make every value column uniform — the width of the longest
  // value (capped by CSS `max-width: 15ch`, beyond which the cell ellipsizes).
  // On mobile, clear the override and let the responsive flex-basis apply.
  sizeColumns() {
    if (!this.multi || !this.columns || !this.columns.length) return;
    // Single-column tables flex to the full row (CSS ds-multi--single) — the
    // pinned uniform width is meaningless there, skip the measure pass.
    if (window.innerWidth < 1025 || this.multi.classList.contains('ds-multi--single')) {
      this.multi.style.removeProperty('--ds-col-w');
      return;
    }
    // Clear the pinned width so columns fall back to max-content (capped),
    // measure the widest, then pin all columns to it for a uniform width.
    this.multi.style.removeProperty('--ds-col-w');
    let max = 0;
    this.columns.forEach((col) => {
      const w = col.getBoundingClientRect().width;
      if (w > max) max = w;
    });
    if (max > 0) this.multi.style.setProperty('--ds-col-w', `${Math.ceil(max)}px`);
  }

  refreshMultiColumn() {
    this.sizeColumns();
    this.sizeRows();
    this.colIndex = Math.min(this.colIndex, this.maxIndex());
    this.applyMultiColumn();
  }

  /* ── Static row sizing (AAS-5016) ───────────────────────────────────── */

  // Long values wrap fully — no line cap (AAS-5173; the former 2-line
  // clamp truncated 3+-line values and leaked the clamped line into the
  // cell padding). Rows live in separate flex stacks (labels + one stack
  // per column), so uniform row heights can't come from CSS alone: measure
  // each row's tallest wrapped cell once (here, and on resize) and pin
  // label + every column's same-index cell to it. The clipped check below
  // is a defensive no-op now — a title tooltip appears only if something
  // still clips.
  sizeRows() {
    const labels = this.multi.querySelector('.ds-multi__labels');
    const labelEls = labels ? Array.from(labels.children) : [];
    const rowCount = Math.max(labelEls.length, ...this.columns.map((c) => c.children.length));

    const rows = [];
    for (let i = 0; i < rowCount; i += 1) {
      const els = this.columns.map((c) => c.children[i]).filter(Boolean);
      if (labelEls[i]) els.push(labelEls[i]);
      rows.push(els);
    }

    // Write → read → write, batched per phase to avoid layout thrash.
    rows.forEach((els) => els.forEach((el) => { el.style.height = ''; }));
    const targets = rows.map((els) => Math.ceil(Math.max(...els.map((el) => el.getBoundingClientRect().height))));
    rows.forEach((els, i) => els.forEach((el) => { el.style.height = `${targets[i]}px`; }));

    this.multi.querySelectorAll('.ds-multi__cell .ds-multi__cell-text').forEach((text) => {
      const clipped = text.scrollHeight > text.clientHeight + 1;
      const cell = text.parentElement;
      if (clipped) cell.title = text.textContent.trim();
      else cell.removeAttribute('title');
    });
  }

  // Keyboard focus (Tab): keep the focused cell on screen — columns page
  // off-screen. (tabindex="0" lives in the Liquid markup.)
  initFocusPaging() {
    this.multi.addEventListener('focusin', (e) => {
      const cell = e.target.closest('.ds-multi__col > .ds-multi__cell');
      if (!cell) return;
      const c = this.columns.indexOf(cell.parentElement);
      if (c >= 0) this.ensureColumnVisible(c);
    });
  }

  // Page the track so column `c` is within the visible window.
  ensureColumnVisible(c) {
    const vis = this.visibleCount();
    let idx = this.colIndex;
    if (c < idx) idx = c;
    else if (c >= idx + vis) idx = c - vis + 1;
    idx = Math.max(0, Math.min(idx, this.maxIndex()));
    if (idx !== this.colIndex) {
      this.colIndex = idx;
      this.applyMultiColumn();
    }
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  /* ---------------------------------------------------------------
     Layer controls
  --------------------------------------------------------------- */

  initLayerControls() {
    this.layerTabs.forEach((tab) => {
      if (!tab.getAttribute('role')) tab.setAttribute('role', 'tab');
      if (!tab.getAttribute('tabindex')) tab.setAttribute('tabindex', '0');
      tab.addEventListener('click', this.handleTabClick.bind(this));
      tab.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          tab.click();
        }
      });
    });
  }

  handleTabClick(event) {
    const tab = event.currentTarget;
    const index = parseInt(tab.dataset.tabIndex ?? '0', 10);

    // Update tab active states
    this.layerTabs.forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });

    tab.classList.add('is-active');
    tab.setAttribute('aria-selected', 'true');

    // Switch visible drawing to the one matching this tab's index
    this.querySelectorAll('.pdp-datasheet__drawing[data-drawing-index]').forEach((drawing) => {
      const matches = parseInt(drawing.dataset.drawingIndex, 10) === index;
      drawing.classList.toggle('is-active', matches);
    });

    this.dispatchEvent(
      new CustomEvent('pdp-datasheet:layer-change', {
        bubbles: true,
        detail: { label: tab.textContent.trim(), index },
      })
    );
  }
}

if (!customElements.get('pdp-datasheet')) {
  customElements.define('pdp-datasheet', PdpDatasheet);
}
